import { useEffect, useState } from "react";
import { Check, Info, RotateCcw, Save } from "lucide-react";
import { Field, StatusPill } from "../components/Ui";
import { assessProject } from "../lib/assessmentEngine";
import type { Project, RegulatoryRegion, WorkType } from "../types/domain";

export function ProjectView({
  project,
  onSave,
}: {
  project: Project;
  onSave: (project: Project) => void;
}) {
  const [draft, setDraft] = useState(project);
  const [saved, setSaved] = useState(false);

  useEffect(() => setDraft(project), [project]);
  const assessment = assessProject(draft);

  const numberValue = (value: string) => (value === "" ? null : Math.max(0, Number(value)));

  const save = () => {
    onSave(draft);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1800);
  };

  return (
    <section className="two-column project-layout">
      <article className="panel form-panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Project identity</span>
            <h2>Confirmed project facts</h2>
          </div>
          {saved && <StatusPill status="success">Saved</StatusPill>}
        </div>

        <div className="form-grid">
          <Field label="Project title" wide>
            <input
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              placeholder="e.g. Hotel guest-room refurbishment"
            />
          </Field>
          <Field label="Project number">
            <input
              value={draft.projectNumber}
              onChange={(event) => setDraft({ ...draft, projectNumber: event.target.value })}
              placeholder="Controlled project identifier"
            />
          </Field>
          <Field label="Current phase">
            <select
              value={draft.currentPhase}
              onChange={(event) => setDraft({ ...draft, currentPhase: event.target.value })}
            >
              <option value="brief">Brief / feasibility</option>
              <option value="pre-construction">Pre-construction / design</option>
              <option value="construction">Construction</option>
              <option value="handover">Handover</option>
              <option value="in-use">In use / maintenance</option>
            </select>
          </Field>
          <Field label="Location">
            <input
              value={draft.location}
              onChange={(event) => setDraft({ ...draft, location: event.target.value })}
              placeholder="City, state"
            />
          </Field>
          <Field
            label="Regulatory jurisdiction"
            hint="Determines which state approval gateways are shown"
          >
            <select
              value={draft.regulatoryRegion ?? "unresolved"}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  regulatoryRegion: event.target.value as RegulatoryRegion,
                })
              }
            >
              <option value="unresolved">Select jurisdiction</option>
              <option value="sarawak">Sarawak</option>
              <option value="sabah">Sabah</option>
              <option value="peninsular-malaysia">Peninsular Malaysia</option>
              <option value="labuan">Labuan</option>
            </select>
          </Field>
          <Field label="Sector">
            <input
              value={draft.sector}
              onChange={(event) => setDraft({ ...draft, sector: event.target.value })}
              placeholder="e.g. Hospitality"
            />
          </Field>
          <Field label="Structure or asset type">
            <input
              value={draft.structureType}
              onChange={(event) => setDraft({ ...draft, structureType: event.target.value })}
              placeholder="Building, civil, infrastructure, plant"
            />
          </Field>
          <Field label="Procurement route">
            <input
              value={draft.procurementRoute}
              onChange={(event) => setDraft({ ...draft, procurementRoute: event.target.value })}
              placeholder="Traditional, design and build, EPC…"
            />
          </Field>
          <Field label="Estimated project value (RM)">
            <input
              type="number"
              min="0"
              value={draft.projectValue ?? ""}
              onChange={(event) =>
                setDraft({ ...draft, projectValue: numberValue(event.target.value) })
              }
            />
          </Field>
          <Field label="Working languages" hint="Comma separated">
            <input
              value={draft.languages.join(", ")}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  languages: event.target.value
                    .split(",")
                    .map((value) => value.trim())
                    .filter(Boolean),
                })
              }
              placeholder="English, Bahasa Malaysia"
            />
          </Field>
          <Field label="Client or project owner" wide>
            <input
              value={draft.clientName}
              onChange={(event) => setDraft({ ...draft, clientName: event.target.value })}
              placeholder="Legal entity or named client"
            />
          </Field>
          <Field label="Work type">
            <select
              value={draft.workType}
              onChange={(event) =>
                setDraft({ ...draft, workType: event.target.value as WorkType })
              }
            >
              <option value="new-construction">New construction</option>
              <option value="renovation">Renovation or alteration</option>
              <option value="maintenance">Maintenance</option>
              <option value="demolition">Demolition</option>
              <option value="other">Other construction work</option>
            </select>
          </Field>
          <Field label="Expected contractor count" hint="Use 2+ if multiple contractors are foreseeable">
            <input
              type="number"
              min="0"
              value={draft.contractorCount ?? ""}
              onChange={(event) =>
                setDraft({ ...draft, contractorCount: numberValue(event.target.value) })
              }
            />
          </Field>
          <Field
            label="Peak workforce"
            hint="A safety and health committee is screened at 40 or more persons"
          >
            <input
              type="number"
              min="0"
              value={draft.peakWorkers ?? ""}
              onChange={(event) =>
                setDraft({ ...draft, peakWorkers: numberValue(event.target.value) })
              }
            />
          </Field>
          <Field label="Start date">
            <input
              type="date"
              value={draft.startDate}
              onChange={(event) => setDraft({ ...draft, startDate: event.target.value })}
            />
          </Field>
          <Field label="Expected completion">
            <input
              type="date"
              value={draft.endDate}
              onChange={(event) => setDraft({ ...draft, endDate: event.target.value })}
            />
          </Field>
          <Field label="Scheduled working days" hint="Notification test uses more than 30">
            <input
              type="number"
              min="0"
              value={draft.workingDays ?? ""}
              onChange={(event) =>
                setDraft({ ...draft, workingDays: numberValue(event.target.value) })
              }
            />
          </Field>
          <Field label="Estimated person-days" hint="Notification test uses more than 500">
            <input
              type="number"
              min="0"
              value={draft.personDays ?? ""}
              onChange={(event) =>
                setDraft({ ...draft, personDays: numberValue(event.target.value) })
              }
            />
          </Field>
          <Field label="Project scope and boundaries" wide>
            <textarea
              rows={5}
              value={draft.description}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              placeholder="Describe the structures, work packages, interfaces, occupied areas and known constraints."
            />
          </Field>
        </div>

        <div className="toggle-row">
          <label>
            <input
              type="checkbox"
              checked={draft.hasDesignWork}
              onChange={(event) => setDraft({ ...draft, hasDesignWork: event.target.checked })}
            />
            <span>
              <strong>Design work is involved</strong>
              <small>Includes preparation or modification of drawings, specifications or calculations.</small>
            </span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={draft.domesticClient}
              onChange={(event) => setDraft({ ...draft, domesticClient: event.target.checked })}
            />
            <span>
              <strong>Domestic client</strong>
              <small>Activates the Regulation 9 duty-transfer workflow.</small>
            </span>
          </label>
        </div>

        <div className="section-divider">
          <span className="eyebrow">Regulatory exposure screen</span>
          <h3>Conditions that activate specialist requirements</h3>
          <p>
            Select every condition that is present or reasonably foreseeable. The regulation
            engine converts these answers into named actions, evidence requirements and official
            source links.
          </p>
        </div>

        <div className="toggle-row exposure-screen">
          <label>
            <input
              type="checkbox"
              checked={draft.usesHazardousChemicals}
              onChange={(event) =>
                setDraft({ ...draft, usesHazardousChemicals: event.target.checked })
              }
            />
            <span>
              <strong>Hazardous chemicals are used or generated</strong>
              <small>Activates USECHH, CLASS, CHRA, exposure-control and health-surveillance checks.</small>
            </span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={draft.hazardousNoisePotential}
              onChange={(event) =>
                setDraft({ ...draft, hazardousNoisePotential: event.target.checked })
              }
            />
            <span>
              <strong>Potential excessive noise exposure</strong>
              <small>Activates identification, noise risk assessment, controls and audiometry workflows.</small>
            </span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={draft.regulatedPlant}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  regulatedPlant: event.target.checked,
                  specialSchemeInspection: event.target.checked
                    ? draft.specialSchemeInspection
                    : false,
                })
              }
            />
            <span>
              <strong>Plant requiring a certificate of fitness</strong>
              <small>Activates design verification, registration, inspection and certificate controls.</small>
            </span>
          </label>
          <label className={!draft.regulatedPlant ? "toggle-disabled" : ""}>
            <input
              type="checkbox"
              disabled={!draft.regulatedPlant}
              checked={draft.specialSchemeInspection}
              onChange={(event) =>
                setDraft({ ...draft, specialSchemeInspection: event.target.checked })
              }
            />
            <span>
              <strong>Special scheme of inspection is proposed</strong>
              <small>Tracks the 2025 alternative inspection scheme pathway for eligible plant.</small>
            </span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={draft.majorHazardPotential}
              onChange={(event) =>
                setDraft({ ...draft, majorHazardPotential: event.target.checked })
              }
            />
            <span>
              <strong>Major industrial hazard potential</strong>
              <small>Activates CIMAH applicability, demonstration and emergency-planning screens.</small>
            </span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={draft.reportableEvent}
              onChange={(event) =>
                setDraft({ ...draft, reportableEvent: event.target.checked })
              }
            />
            <span>
              <strong>Reportable incident, occurrence or disease</strong>
              <small>Activates NADOPOD notification, investigation and statutory record actions.</small>
            </span>
          </label>
          <label className="exposure-confirmation">
            <input
              type="checkbox"
              checked={draft.regulatoryExposureConfirmed}
              onChange={(event) =>
                setDraft({ ...draft, regulatoryExposureConfirmed: event.target.checked })
              }
            />
            <span>
              <strong>Exposure screen confirmed</strong>
              <small>
                Confirms that every condition above has been checked against the current scope,
                work methods, substances, plant and expected workforce.
              </small>
            </span>
          </label>
        </div>

        <div className="form-actions">
          <button className="button button-secondary" onClick={() => setDraft(project)}>
            <RotateCcw size={17} />
            Discard changes
          </button>
          <button className="button button-primary" onClick={save}>
            <Save size={17} />
            Save confirmed facts
          </button>
        </div>
      </article>

      <aside className="sticky-column">
        <article className="panel decision-card">
          <div className="decision-icon">
            <Check size={22} />
          </div>
          <span className="eyebrow">Regulatory result</span>
          <h2>{assessment.applies ? "CDM workflow active" : "Outside current workflow"}</h2>
          <p>
            Based on the recorded project facts. Conclusions are retained as accountable project
            decisions with source links and evidence.
          </p>
          <dl>
            <div>
              <dt>Multiple contractors</dt>
              <dd>{assessment.multipleContractors ? "Yes" : "Not recorded"}</dd>
            </div>
            <div>
              <dt>Notification threshold</dt>
              <dd>{assessment.notifiable ? "Exceeded" : "Not exceeded"}</dd>
            </div>
            <div>
              <dt>CDM duties</dt>
              <dd>{assessment.cdmRules.length}</dd>
            </div>
            <div>
              <dt>Related checks</dt>
              <dd>{assessment.relatedRules.length + assessment.approvalGateways.length}</dd>
            </div>
          </dl>
        </article>

        {assessment.warnings.length > 0 && (
          <article className="panel warning-panel">
            <div className="panel-header compact">
              <div>
                <Info size={19} />
                <h3>Unresolved facts</h3>
              </div>
            </div>
            <ul>
              {assessment.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </article>
        )}
      </aside>
    </section>
  );
}
